import SiteCoreLinks from "@/components/SiteCoreLinks";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Shield, Mail, Phone } from "lucide-react";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";

const SITE = "https://nyclanguageinstitute.com";
const TITLE = "นโยบายความเป็นส่วนตัว (PDPA Privacy Policy) | NYC Language Institute";
const DESC =
  "นโยบายคุ้มครองข้อมูลส่วนบุคคล (PDPA) ของ NYC Legal & Notary Service Co., Ltd. ปรึกษาฟรีก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ";

export const Route = createFileRoute("/privacy")({
  head: () => ({
    meta: [
      { title: ctaTitle(TITLE, "/privacy", "th")},
      { name: "description", content: ctaDesc(DESC, "/privacy", "th")},
      { name: "robots", content: "index,follow" },
      { property: "og:title", content: ctaTitle(TITLE, "/privacy", "th")},
      { property: "og:description", content: ctaDesc(DESC, "/privacy", "th")},
      { property: "og:url", content: `${SITE}/privacy` },
    ],
    links: [
      { rel: "canonical", href: `${SITE}/privacy` },
      ...hreflangLinks("/privacy"),
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: TITLE,
          description: DESC,
          url: `${SITE}/privacy`,
          inLanguage: "th-TH",
          isPartOf: { "@type": "WebSite", name: "NYC Language Institute", url: SITE },
          about: { "@type": "Thing", name: "PDPA Privacy Policy" },
        }),
      },
    ],
  }),
  component: PrivacyPage,
});

const SECTIONS: { h: string; body: string[] }[] = [
  {
    h: "1. ผู้ควบคุมข้อมูลส่วนบุคคล",
    body: [
      "บริษัท เอ็นวายซี ลีเกิล แอนด์ โนตารี เซอร์วิส จำกัด (\"บริษัท\" หรือ \"เรา\") เลขทะเบียน 0105556167091 สำนักงานใหญ่ 61 ซอยลาดพร้าว 95 (ปรางค์ทิพย์) แขวงคลองเจ้าคุณสิงห์ เขตวังทองหลาง กรุงเทพฯ 10310",
      "บริษัทดำเนินการตามพระราชบัญญัติคุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562 (PDPA) อย่างเคร่งครัด",
    ],
  },
  {
    h: "2. ข้อมูลส่วนบุคคลที่เราเก็บรวบรวม",
    body: [
      "ข้อมูลระบุตัวตน: ชื่อ-นามสกุล, เลขบัตรประชาชน/พาสปอร์ต, วันเกิด, สัญชาติ (เฉพาะที่จำเป็นต่อการแปล/รับรองเอกสาร)",
      "ข้อมูลติดต่อ: เบอร์โทรศัพท์, อีเมล, LINE ID, ที่อยู่จัดส่งเอกสาร",
      "ข้อมูลเอกสารต้นฉบับที่ลูกค้าส่งให้แปล/รับรอง (สูติบัตร ทะเบียนสมรส transcripts สัญญา ฯลฯ)",
      "ข้อมูลการชำระเงิน: เลขบัญชี/บัตรเครดิต (ประมวลผลผ่าน payment gateway ที่ได้รับมาตรฐาน PCI-DSS เท่านั้น เราไม่จัดเก็บเลขบัตรเต็ม)",
      "ข้อมูลทางเทคนิคจากเว็บไซต์: IP, cookies, อุปกรณ์, browser, หน้าที่เข้าชม (เพื่อ analytics และความปลอดภัย)",
    ],
  },
  {
    h: "3. วัตถุประสงค์ในการประมวลผล",
    body: [
      "ดำเนินงานแปลภาษา รับรอง Notary, MFA, สถานทูต, NAATI และยื่นวีซ่าตามที่ลูกค้าร้องขอ",
      "ติดต่อกลับ ส่งใบเสนอราคา ออกใบกำกับภาษี และจัดส่งเอกสาร",
      "ปฏิบัติตามกฎหมายฟอกเงิน (AMLA), กฎหมายภาษี, และคำสั่งศาล/หน่วยงานราชการ",
      "พัฒนาคุณภาพบริการ วิเคราะห์การใช้งาน และส่งข่าวสาร/โปรโมชั่น (เฉพาะกรณีได้รับความยินยอม สามารถยกเลิกได้ทุกเมื่อ)",
    ],
  },
  {
    h: "4. ฐานทางกฎหมาย (Lawful Basis)",
    body: [
      "การปฏิบัติตามสัญญา (Contract) — ดำเนินงานตามที่ตกลง",
      "หน้าที่ตามกฎหมาย (Legal Obligation) — เก็บเอกสารตามกฎหมายบัญชี/ภาษี/AMLA อย่างน้อย 5-10 ปี",
      "ประโยชน์อันชอบธรรม (Legitimate Interest) — ป้องกันการทุจริตและรักษาความปลอดภัยระบบ",
      "ความยินยอม (Consent) — สำหรับการตลาดและ cookies ที่ไม่จำเป็น",
    ],
  },
  {
    h: "5. การเปิดเผยข้อมูลแก่บุคคลที่สาม",
    body: [
      "หน่วยราชการ: กระทรวงการต่างประเทศ, สถานทูต, กรมการกงสุล, สำนักงานตรวจคนเข้าเมือง, กรมสรรพากร — เฉพาะที่จำเป็นต่อการรับรอง/ยื่นเอกสารตามที่ลูกค้ามอบหมาย",
      "ผู้ให้บริการที่บริษัทใช้: courier (Kerry, DHL, EMS), payment gateway, cloud hosting (Cloudflare, Google Cloud) — ทุกรายมีสัญญา DPA (Data Processing Agreement) กับบริษัท",
      "เราไม่ขาย ไม่ให้เช่า และไม่แลกเปลี่ยนข้อมูลส่วนบุคคลกับบุคคลที่สามเพื่อการตลาดโดยเด็ดขาด",
    ],
  },
  {
    h: "6. การเก็บรักษาและระยะเวลา",
    body: [
      "เอกสารงานแปล: 7 ปีนับจากวันส่งมอบ (ตามกฎหมายบัญชีและเพื่อรองรับการขอสำเนาภายหลัง)",
      "เอกสาร AMLA/CDD: 10 ปีตามกฎหมายฟอกเงิน",
      "ข้อมูลการตลาด: จนกว่าผู้ใช้จะถอนความยินยอม",
      "ข้อมูล log/cookies: 12 เดือน",
    ],
  },
  {
    h: "7. มาตรการรักษาความปลอดภัย",
    body: [
      "เข้ารหัสข้อมูลขณะส่ง (TLS 1.3) และขณะจัดเก็บ (AES-256)",
      "ควบคุมการเข้าถึงด้วยสิทธิ์ตามบทบาท (RBAC) + 2FA สำหรับผู้ดูแลระบบทุกคน",
      "พนักงานทุกคนลงนาม NDA และผ่านการอบรม PDPA ประจำปี",
      "ตู้นิรภัยและห้องเก็บเอกสารกระดาษมีระบบควบคุมการเข้า-ออก และ CCTV 24 ชม.",
      "Audit log ทุกการเข้าถึงข้อมูลลูกค้า ตรวจสอบย้อนหลังได้",
    ],
  },
  {
    h: "8. สิทธิของเจ้าของข้อมูลส่วนบุคคล",
    body: [
      "สิทธิเข้าถึง (Right of Access) — ขอสำเนาข้อมูลของท่าน",
      "สิทธิแก้ไข (Right to Rectification) — แก้ไขข้อมูลที่ไม่ถูกต้อง",
      "สิทธิลบ (Right to Erasure) — ลบข้อมูลเมื่อหมดความจำเป็น (ภายใต้ข้อยกเว้นทางกฎหมาย)",
      "สิทธิคัดค้าน/จำกัดการประมวลผล",
      "สิทธิโอนย้ายข้อมูล (Data Portability)",
      "สิทธิถอนความยินยอม — ยกเลิกการรับข่าวสารได้ทันที",
      "สิทธิร้องเรียนต่อสำนักงานคณะกรรมการคุ้มครองข้อมูลส่วนบุคคล (PDPC)",
    ],
  },
  {
    h: "9. Cookies และเทคโนโลยีติดตาม",
    body: [
      "เว็บไซต์ใช้ cookies ที่จำเป็น (session, security) และ analytics cookies (Google Analytics, Cloudflare) เพื่อปรับปรุงการใช้งาน",
      "ท่านสามารถปิด non-essential cookies ได้ผ่านการตั้งค่า browser โดยไม่กระทบการใช้งานหลัก",
    ],
  },
  {
    h: "10. การโอนข้อมูลข้ามประเทศ",
    body: [
      "บางบริการอาจมีการประมวลผลในต่างประเทศ (เช่น Cloudflare CDN, NAATI Australia) — เราเลือกผู้ให้บริการที่มีมาตรฐานคุ้มครองข้อมูลเทียบเท่าหรือสูงกว่า PDPA และมีสัญญารองรับ",
    ],
  },
  {
    h: "11. การปรับปรุงนโยบาย",
    body: [
      "นโยบายฉบับนี้มีผลตั้งแต่วันที่ 1 มกราคม 2569 — หากมีการแก้ไขสาระสำคัญ บริษัทจะแจ้งให้ทราบล่วงหน้าผ่านเว็บไซต์และอีเมล",
    ],
  },
];

function PrivacyPage() {
  return (
    <main className="min-h-screen bg-navy-deep text-white">
      <section className="border-b border-white/10 bg-gradient-to-b from-[#0a1a3d] to-[#0d2150] px-4 py-14">
        <div className="mx-auto max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-amber-400/30 bg-amber-400/10 px-4 py-1.5 text-xs font-semibold text-amber-300">
            <Shield className="h-3.5 w-3.5" /> PDPA Compliant · พ.ร.บ. คุ้มครองข้อมูลส่วนบุคคล พ.ศ. 2562
          </div>
          <h1 className="mt-5 text-3xl font-extrabold sm:text-5xl">
            นโยบายความเป็นส่วนตัว
          </h1>
          <p className="mt-3 max-w-2xl text-white/75">
            ความเป็นส่วนตัวและความปลอดภัยของข้อมูลลูกค้าเป็นหัวใจของการดำเนินธุรกิจของเรา
            นโยบายนี้อธิบายวิธีที่เราเก็บ ใช้ และคุ้มครองข้อมูลของท่านตามกฎหมาย PDPA
          </p>
          <p className="mt-2 text-xs text-white/80">มีผล: 1 มกราคม 2569 · ปรับปรุงล่าสุด: พฤษภาคม 2569</p>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-4xl space-y-8">
          {SECTIONS.map((s) => (
            <article key={s.h} className="rounded-2xl border border-white/10 bg-white/[0.03] p-6 sm:p-8">
              <h2 className="text-lg font-bold text-amber-300 sm:text-xl">{s.h}</h2>
              <ul className="mt-4 space-y-2 text-sm leading-relaxed text-white/80">
                {s.body.map((line, i) => (
                  <li key={i} className="flex gap-3">
                    <span className="mt-2 h-1.5 w-1.5 flex-none rounded-full bg-amber-400" />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>
            </article>
          ))}

          <article className="rounded-2xl border border-amber-400/30 bg-gradient-to-br from-amber-500/10 to-amber-500/5 p-6 sm:p-8">
            <h2 className="text-lg font-bold sm:text-xl">12. ติดต่อเจ้าหน้าที่คุ้มครองข้อมูล (DPO)</h2>
            <p className="mt-3 text-sm text-white/80">
              หากท่านต้องการใช้สิทธิ์ตามกฎหมาย PDPA สอบถามข้อมูล หรือร้องเรียนเรื่องการประมวลผลข้อมูลส่วนบุคคล
              สามารถติดต่อเจ้าหน้าที่ DPO ของบริษัทได้ตลอด 24 ชม.
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              <a href="mailto:dpo@nyclegal.co.th" className="inline-flex items-center gap-2 rounded-xl bg-amber-500 px-5 py-3 text-sm font-bold text-navy-deep hover:bg-amber-400">
                <Mail className="h-4 w-4" /> dpo@nyclegal.co.th
              </a>
              <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-xl border border-white/20 bg-white/5 px-5 py-3 text-sm font-bold text-white hover:bg-white/10">
                <Phone className="h-4 w-4" /> 083-249-4999
              </a>
            </div>
            <p className="mt-5 text-xs text-white/80">
              ดูเพิ่มเติม: <Link to="/terms" className="underline hover:text-amber-300">ข้อกำหนดการใช้บริการ</Link> ·{" "}
              <Link to="/trust" className="underline hover:text-amber-300">ความน่าเชื่อถือและการรับรอง</Link>
            </p>
          </article>
        </div>
      </section>
          <SiteCoreLinks />
    </main>
  );
}
